/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * ConversationList - Inbox view showing all conversations
 */

import { useState, useEffect, useCallback } from 'react'
import { MessageSquare, User, Search, Plus, Loader2, Archive, Inbox } from 'lucide-react'
import messagingService from '../../services/messagingService'
import { createLogger } from '../../utils/logger'

const logger = createLogger('ConversationList')

const ConversationList = ({
  currentUserId,
  onSelectConversation,
  selectedConversationId,
  onNewConversation,
  refreshTrigger = 0
}) => {
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showArchived, setShowArchived] = useState(false)

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true)
      const response = await messagingService.getConversations({
        status: showArchived ? 'archived' : 'active',
        limit: 50
      })
      if (response.success) {
        setConversations(response.conversations || [])
      }
    } catch (error) {
      logger.error('Error loading conversations:', error)
    } finally {
      setLoading(false)
    }
  }, [showArchived])

  useEffect(() => {
    loadConversations()
  }, [loadConversations, refreshTrigger])

  const formatTime = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit'
      })
    } else if (diffDays === 1) {
      return 'Yesterday'
    } else if (diffDays < 7) {
      return date.toLocaleDateString(undefined, { weekday: 'short' })
    }
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    })
  }

  const getOtherParticipant = (conversation) => {
    if (conversation.coachId === currentUserId) {
      return conversation.participant
    }
    return conversation.coach
  }

  const getUnreadCount = (conversation) => {
    if (conversation.coachId === currentUserId) {
      return conversation.coachUnreadCount || 0
    }
    return conversation.participantUnreadCount || 0
  }

  const filteredConversations = conversations.filter((conv) => {
    if (!searchQuery) return true
    const other = getOtherParticipant(conv)
    const name = `${other?.firstName || ''} ${other?.lastName || ''}`.toLowerCase()
    return name.includes(searchQuery.toLowerCase())
  })

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Messages</h2>
          <button
            onClick={onNewConversation}
            className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            New
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600
              bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100
              placeholder:text-gray-500 dark:placeholder:text-gray-400
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setShowArchived(false)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              !showArchived
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <Inbox className="w-4 h-4" />
            Inbox
          </button>
          <button
            onClick={() => setShowArchived(true)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              showArchived
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <Archive className="w-4 h-4" />
            Archived
          </button>
        </div>
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              {showArchived
                ? 'No archived conversations'
                : searchQuery
                  ? 'No conversations found'
                  : 'No conversations yet'}
            </p>
            {!showArchived && !searchQuery && (
              <button
                onClick={onNewConversation}
                className="mt-3 text-primary-600 dark:text-primary-400 hover:underline text-sm"
              >
                Start a new conversation
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {filteredConversations.map((conversation) => {
              const other = getOtherParticipant(conversation)
              const unreadCount = getUnreadCount(conversation)
              const lastMessage = conversation.messages?.[0]
              const isSelected = selectedConversationId === conversation.id

              return (
                <button
                  key={conversation.id}
                  onClick={() => onSelectConversation(conversation.id)}
                  className={`w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
                    isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Name and time */}
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={`font-medium truncate ${
                            unreadCount > 0
                              ? 'text-gray-900 dark:text-gray-100'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {other?.firstName} {other?.lastName}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                          {formatTime(conversation.lastMessageAt)}
                        </span>
                      </div>

                      {/* Subject or regarding */}
                      {(conversation.subject || conversation.regardingChild) && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {conversation.subject ||
                            `About: ${conversation.regardingChild?.firstName}`}
                        </p>
                      )}

                      {/* Last message preview */}
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <p
                          className={`text-sm truncate ${
                            unreadCount > 0
                              ? 'text-gray-900 dark:text-gray-100 font-medium'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {lastMessage?.senderId === currentUserId && (
                            <span className="text-gray-400 dark:text-gray-500">You: </span>
                          )}
                          {lastMessage?.content || 'No messages yet'}
                        </p>

                        {/* Unread badge */}
                        {unreadCount > 0 && (
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-600 text-white text-xs flex items-center justify-center">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default ConversationList
